import { useState } from "react";
import { Film, CloudUpload, CheckCircle, AlertCircle, Loader2, Bug, Unplug, LogOut, Menu, X, TrendingUp } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import PixallyAd from "@/components/PixallyAd";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function openOAuthPopup(url, onDone) {
  const w = 600, h = 700;
  const left = window.screenX + (window.outerWidth - w) / 2;
  const top = window.screenY + (window.outerHeight - h) / 2;
  const popup = window.open(url, "gemvault_oauth", `width=${w},height=${h},left=${left},top=${top},toolbar=no,menubar=no`);
  const listener = (event) => {
    if (event.data?.type === "oauth_callback") {
      window.removeEventListener("message", listener);
      onDone(event.data);
    }
  };
  window.addEventListener("message", listener);
  const poll = setInterval(() => {
    if (popup && popup.closed) {
      clearInterval(poll);
      window.removeEventListener("message", listener);
      onDone({ type: "oauth_callback", status: "check" });
    }
  }, 500);
}

export default function Sidebar({ health, transferCount, onShowTransfers, showTransfers, onShowVideos, onHealthRefresh, userEmail, onLogout, isAdmin, showAdmin, onShowAdmin }) {
  const vimeoOk = health?.vimeo?.connected;
  const dropboxOk = health?.dropbox?.connected;
  const gdriveOk = health?.gdrive?.connected;
  const [reconnecting, setReconnecting] = useState("");
  const [bugOpen, setBugOpen] = useState(false);
  const [bugMessage, setBugMessage] = useState("");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleDisconnect = async (service) => {
    const label = service === "vimeo" ? "Vimeo" : service === "dropbox" ? "Dropbox" : "Google Drive";
    try {
      await axios.post(`${API}/disconnect/${service}`);
      toast.success(`${label} disconnected`);
      if (onHealthRefresh) onHealthRefresh();
    } catch {
      toast.error(`Failed to disconnect ${label}`);
    }
  };

  const handleReconnect = async (service) => {
    setReconnecting(service);
    try {
      const res = await axios.get(`${API}/auth/${service}`);
      if (res.data.url) {
        openOAuthPopup(res.data.url, async (data) => {
          setReconnecting("");
          if (data.status === "connected") {
            const label = service === "vimeo" ? "Vimeo" : service === "dropbox" ? "Dropbox" : "Google Drive";
            toast.success(`${label} reconnected`);
          }
          if (onHealthRefresh) onHealthRefresh();
        });
      } else {
        toast.error(res.data.error || `${service} OAuth not configured`);
        setReconnecting("");
      }
    } catch {
      toast.error(`Could not start ${service} reconnection`);
      setReconnecting("");
    }
  };

  return (
    <aside data-testid="sidebar" className="w-full md:w-64 flex-shrink-0 border-r border-[#222630] bg-[#0f1114] md:sticky md:top-[32px] md:h-[calc(100vh-32px)] z-40 flex flex-col">
      {/* Mobile header bar */}
      <div className="flex md:hidden items-center justify-between px-4 py-3 border-b border-[#222630]">
        <div className="flex items-center gap-2">
          <img src="/gemvault-logo-64.png" alt="" className="h-5 w-5 rounded" />
          <div>
            <span className="text-sm font-bold text-[#f4f4f5]" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>GemVault</span>
            <span className="section-label ml-2 text-[8px]">// vimeo backup tool</span>
          </div>
        </div>
        <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="text-[#a1a1aa] hover:text-[#c6a96f] transition-colors p-1">
          {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile dropdown menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-b border-[#222630] bg-[#0f1114]">
          <nav className="flex flex-col gap-1 p-3">
            <button onClick={() => { onShowVideos(); setMobileMenuOpen(false); }} className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all w-full text-left ${!showTransfers ? "ug-nav-active" : "text-[#71717a]"}`}>
              <Film className="h-4 w-4" /> Videos
            </button>
            <button onClick={() => { onShowTransfers(); setMobileMenuOpen(false); }} className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all w-full text-left ${showTransfers ? "ug-nav-active" : "text-[#71717a]"}`}>
              <CloudUpload className="h-4 w-4" /> Transfers
              {transferCount > 0 && <span className="ml-auto bg-[rgba(198,169,111,0.15)] text-[#c6a96f] text-[10px] font-mono px-2 py-0.5 rounded-full">{transferCount}</span>}
            </button>
          </nav>
          {userEmail && (
            <div className="px-4 py-3 border-t border-[#222630] flex items-center justify-between">
              <span className="text-[11px] text-[#a1a1aa] truncate">{userEmail}</span>
              <button onClick={onLogout} className="text-[#52525b] hover:text-[#c47272] transition-colors p-1 rounded-md"><LogOut className="h-3.5 w-3.5" /></button>
            </div>
          )}
        </div>
      )}

      {/* Desktop sidebar content */}
      <div className="hidden md:flex md:flex-col md:flex-1">
        {/* Brand */}
        <div className="p-6 border-b border-[#222630]">
          <div className="section-label mb-2">// vimeo backup tool</div>
          <h2 className="text-lg font-bold tracking-tight text-[#f4f4f5] flex items-center gap-2.5" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
            <img src="/gemvault-logo-64.png" alt="" className="h-6 w-6 rounded" />
            GemVault
          </h2>
          <a
            href="https://uncutgems.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[11px] text-[#71717a] mt-1 font-mono tracking-wide inline-block hover:text-[#c6a96f] transition-colors"
          >
            by Uncut Gems
          </a>
        </div>

        {/* User */}
        {userEmail && (
          <div className="px-4 py-3 border-b border-[#222630] flex items-center justify-between">
            <span className="text-[11px] text-[#a1a1aa] truncate">{userEmail}</span>
            <button data-testid="logout-btn" onClick={onLogout} className="text-[#52525b] hover:text-[#c47272] transition-colors p-1 rounded-md hover:bg-[#c47272]/5" title="Log out">
              <LogOut className="h-3.5 w-3.5" />
            </button>
          </div>
        )}

        {/* Navigation */}
        <nav className="flex flex-col gap-1 p-3 flex-1">
        <button
          data-testid="nav-videos"
          onClick={onShowVideos}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all w-full text-left ${
            !showTransfers && !showAdmin ? "ug-nav-active" : "text-[#71717a] hover:text-[#c6a96f] hover:bg-[rgba(198,169,111,0.05)]"
          }`}
        >
          <Film className="h-4 w-4" />
          Videos
        </button>
        <button
          data-testid="nav-transfers"
          onClick={onShowTransfers}
          className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all w-full text-left ${
            showTransfers ? "ug-nav-active" : "text-[#71717a] hover:text-[#c6a96f] hover:bg-[rgba(198,169,111,0.05)]"
          }`}
        >
          <CloudUpload className="h-4 w-4" />
          Transfers
          {transferCount > 0 && (
            <span data-testid="active-transfers-badge" className="ml-auto bg-[rgba(198,169,111,0.15)] text-[#c6a96f] text-[10px] font-mono px-2 py-0.5 rounded-full">
              {transferCount}
            </span>
          )}
        </button>
        {isAdmin && (
          <button
            data-testid="nav-admin"
            onClick={onShowAdmin}
            className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm transition-all w-full text-left ${
              showAdmin ? "ug-nav-active" : "text-[#71717a] hover:text-[#c6a96f] hover:bg-[rgba(198,169,111,0.05)]"
            }`}
          >
            <TrendingUp className="h-4 w-4" />
            Admin
          </button>
        )}

        <PixallyAd />
      </nav>

      {/* Connection Status */}
      <div className="p-4 border-t border-[#222630] space-y-2">
        <div className="section-label mb-3">// connections</div>

        {/* Vimeo */}
        {vimeoOk ? (
          <button
            data-testid="vimeo-status"
            onClick={() => handleDisconnect("vimeo")}
            className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg text-[#5fa87a] hover:bg-[#c47272]/5 transition-colors group"
          >
            <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate flex-1 text-left">Vimeo ({health?.vimeo?.user})</span>
            <Unplug className="h-3 w-3 text-transparent group-hover:text-[#c47272] transition-colors" />
          </button>
        ) : (
          <button
            data-testid="vimeo-reconnect"
            onClick={() => handleReconnect("vimeo")}
            disabled={reconnecting === "vimeo"}
            className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg text-[#c47272] hover:bg-[#c47272]/5 transition-colors group disabled:opacity-50"
          >
            {reconnecting === "vimeo" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            )}
            <span className="flex-1 text-left">Vimeo (disconnected)</span>
            <span className="text-[9px] font-mono text-[#c47272]/60 group-hover:text-[#c47272] transition-colors">
              {reconnecting === "vimeo" ? "..." : "reconnect"}
            </span>
          </button>
        )}

        {/* Dropbox */}
        {dropboxOk ? (
          <button
            data-testid="dropbox-status"
            onClick={() => handleDisconnect("dropbox")}
            className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg text-[#5fa87a] hover:bg-[#c47272]/5 transition-colors group"
          >
            <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate flex-1 text-left">Dropbox ({health?.dropbox?.user})</span>
            <Unplug className="h-3 w-3 text-transparent group-hover:text-[#c47272] transition-colors" />
          </button>
        ) : (
          <button
            data-testid="dropbox-reconnect"
            onClick={() => handleReconnect("dropbox")}
            disabled={reconnecting === "dropbox"}
            className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg text-[#c47272] hover:bg-[#c47272]/5 transition-colors group disabled:opacity-50"
          >
            {reconnecting === "dropbox" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
            ) : (
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0" />
            )}
            <span className="flex-1 text-left">Dropbox (disconnected)</span>
            <span className="text-[9px] font-mono text-[#c47272]/60 group-hover:text-[#c47272] transition-colors">
              {reconnecting === "dropbox" ? "..." : "reconnect"}
            </span>
          </button>
        )}

        {/* Google Drive */}
        {gdriveOk ? (
          <button
            data-testid="gdrive-status"
            onClick={() => handleDisconnect("gdrive")}
            className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg text-[#5fa87a] hover:bg-[#c47272]/5 transition-colors group"
          >
            <CheckCircle className="h-3.5 w-3.5 flex-shrink-0" />
            <span className="truncate flex-1 text-left">Google Drive ({health?.gdrive?.user})</span>
            <Unplug className="h-3 w-3 text-transparent group-hover:text-[#c47272] transition-colors" />
          </button>
        ) : health?.gdrive ? (
          <button
            data-testid="gdrive-reconnect"
            onClick={() => handleReconnect("gdrive")}
            disabled={reconnecting === "gdrive"}
            className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg text-[#71717a] hover:bg-[rgba(198,169,111,0.05)] hover:text-[#c6a96f] transition-colors group disabled:opacity-50"
          >
            {reconnecting === "gdrive" ? (
              <Loader2 className="h-3.5 w-3.5 animate-spin flex-shrink-0" />
            ) : (
              <CloudUpload className="h-3.5 w-3.5 flex-shrink-0" />
            )}
            <span className="flex-1 text-left">Google Drive</span>
            <span className="text-[9px] font-mono text-[#52525b] group-hover:text-[#c6a96f] transition-colors">
              {reconnecting === "gdrive" ? "..." : "connect"}
            </span>
          </button>
        ) : null}
      </div>

      {/* Report Bug */}
      <div className="p-4 border-t border-[#222630]">
        <button
          data-testid="report-bug-btn"
          onClick={() => setBugOpen(true)}
          className="flex items-center gap-2 text-xs w-full px-2 py-1.5 rounded-lg text-[#52525b] hover:text-[#c6a96f] hover:bg-[rgba(198,169,111,0.05)] transition-colors"
        >
          <Bug className="h-3.5 w-3.5" />
          <span>Report a Bug</span>
        </button>
      </div>

      </div> {/* end desktop sidebar content */}

      {/* Bug Report Modal */}
      <AlertDialog open={bugOpen} onOpenChange={setBugOpen}>
        <AlertDialogContent className="border-[#2a2e36] rounded-2xl max-w-md" style={{ background: '#1a1d21' }}>
          <AlertDialogHeader>
            <AlertDialogTitle className="text-xl text-[#f4f4f5] tracking-tight" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>
              Report a Bug
            </AlertDialogTitle>
            <AlertDialogDescription className="text-sm text-[#a1a1aa] mt-1">
              Tell us what went wrong and we'll take a look.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <textarea
            data-testid="bug-message-input"
            value={bugMessage}
            onChange={(e) => setBugMessage(e.target.value)}
            placeholder="What happened? What did you expect?"
            rows={4}
            className="w-full mt-3 p-3 rounded-xl text-sm bg-[#21252b] border border-[#2a2e36] text-[#e4e4e7] placeholder:text-[#52525b] focus:outline-none focus:border-[#a08a58] resize-none"
          />
          <p className="text-[10px] text-[#52525b] font-mono tracking-wider mt-2">
            // this opens your email client with the details pre-filled
          </p>
          <AlertDialogFooter className="mt-4 gap-3">
            <AlertDialogCancel
              className="border-[#363b45] text-[#a1a1aa] hover:text-[#c6a96f] hover:bg-[rgba(198,169,111,0.05)] rounded-xl"
              style={{ background: 'transparent' }}
            >
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="btn-gold rounded-xl font-semibold text-sm"
              onClick={() => {
                const subject = encodeURIComponent("GemVault Bug Report");
                const browser = navigator.userAgent;
                const body = encodeURIComponent(
                  `${bugMessage}\n\n---\nBrowser: ${browser}\nURL: ${window.location.href}\nTime: ${new Date().toISOString()}`
                );
                window.open(`mailto:hello@uncutgems.com?subject=${subject}&body=${body}`, '_blank');
                setBugMessage("");
                setBugOpen(false);
                toast.success("Opening your email client — thanks for the report");
              }}
              disabled={!bugMessage.trim()}
            >
              Send Report
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </aside>
  );
}
